// Impresion de una Boleta de Labores individual. Mismo patron que boletaImprimir.ts:
// construye el HTML completo y lo manda a un iframe oculto para imprimir/guardar PDF.

interface BoletaImprimible {
  numero: string;
  fecha: string;
  supervisor_nombre: string | null;
  finca_nombre: string | null;
  lote_num: string | null;
  estadio_codigo: string | null;
  estado: string;
  costo_total: number | null;
  observacion: string | null;
}

interface BloqueImprimible { bloque_num: string | null }
interface LaborImprimible { labor_nombre: string; modalidad: string; unidad: string; cantidad: number; horas: number; costo_labor: number }
interface TrabajadorImprimible {
  trabajador_codigo: string | null;
  trabajador_nombre: string;
  labor_nombre: string | null;
  lote_num?: string | null;
  horas_ordinarias: number | null;
  horas_extra: number | null;
  monto_calculado: number | null;
  estado_asistencia?: string | null;
}

function fmtFecha(s?: string | null) {
  if (!s) return '';
  return new Date(`${s}T00:00:00`).toLocaleDateString('es-CR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function fmtCRC(v?: number | null) {
  const n = Number(v ?? 0);
  return 'CRC ' + n.toLocaleString('es-CR', { maximumFractionDigits: 2 });
}

function fmtHoras(v?: number | null) {
  const n = Number(v ?? 0);
  return n ? n.toFixed(2) : '-';
}

export function imprimirBoletaLabores({
  boleta,
  bloques,
  labores,
  trabajadores,
}: {
  boleta: BoletaImprimible;
  bloques: BloqueImprimible[];
  labores: LaborImprimible[];
  trabajadores: TrabajadorImprimible[];
}) {
  const filasTrabajadores = trabajadores.map((t) => `
    <tr>
      <td>${t.trabajador_codigo ?? ''}</td>
      <td>${t.trabajador_nombre}</td>
      <td>${t.labor_nombre ?? ''}</td>
      <td>${t.lote_num ?? boleta.lote_num ?? ''}</td>
      <td class="num">${fmtHoras(t.estado_asistencia === 'vacaciones' ? 8 : t.horas_ordinarias)}</td>
      <td class="num">${fmtHoras(t.estado_asistencia === 'vacaciones' ? 0 : t.horas_extra)}</td>
      <td class="num">${fmtCRC(t.monto_calculado)}</td>
    </tr>`).join('');

  const filasLabores = labores.map((l) => `
    <tr>
      <td>${l.labor_nombre}</td>
      <td>${l.modalidad} / ${l.unidad}</td>
      <td class="num">${fmtHoras(l.horas)}</td>
      <td class="num">${Number(l.cantidad || 0) || '-'}</td>
      <td class="num">${fmtCRC(l.costo_labor)}</td>
    </tr>`).join('');

  const bloquesTxt = bloques.map((b) => b.bloque_num).filter(Boolean).join(', ') || '-';

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Boleta ${boleta.numero}</title>
  <style>
    @page { size: letter; margin: 12mm; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 11px; color: #111; }
    h1 { font-size: 16px; margin: 0 0 2px; }
    .sub { color: #555; margin-bottom: 12px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 14px; }
    th, td { border: 1px solid #999; padding: 4px 6px; text-align: left; }
    th { background: #eee; }
    td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
    .campos { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px 16px; margin-bottom: 14px; }
    .campo label { display: block; font-size: 9px; text-transform: uppercase; color: #666; }
    .obs { border: 1px solid #999; padding: 6px 8px; margin-bottom: 10px; font-size: 10px; }
    .firma { margin-top: 40px; display: grid; grid-template-columns: 1fr 1fr; gap: 30px; }
    .firma div { border-top: 1px solid #333; padding-top: 4px; text-align: center; font-size: 10px; }
  </style></head>
  <body>
    <h1>Boleta de Labores ${boleta.numero}</h1>
    <div class="sub">Estado: ${boleta.estado.toUpperCase()}</div>
    <div class="campos">
      <div class="campo"><label>Fecha</label>${fmtFecha(boleta.fecha)}</div>
      <div class="campo"><label>Finca / Lote</label>${boleta.finca_nombre ?? '-'} / ${boleta.lote_num ?? '-'}</div>
      <div class="campo"><label>Estadio</label>${boleta.estadio_codigo ?? '-'}</div>
      <div class="campo"><label>Bloques</label>${bloquesTxt}</div>
      <div class="campo"><label>Supervisor</label>${boleta.supervisor_nombre ?? '-'}</div>
      <div class="campo"><label>Costo total</label>${fmtCRC(boleta.costo_total)}</div>
    </div>
    ${boleta.observacion ? `<div class="obs">${boleta.observacion}</div>` : ''}
    <table>
      <thead><tr><th>Labor</th><th>Modalidad</th><th class="num">Horas</th><th class="num">Unid.</th><th class="num">Costo</th></tr></thead>
      <tbody>${filasLabores || '<tr><td colspan="5">Sin labores</td></tr>'}</tbody>
    </table>
    <table>
      <thead><tr><th>Codigo</th><th>Empleado</th><th>Labor</th><th>Lote</th><th class="num">H. Norm.</th><th class="num">H. Extra</th><th class="num">Monto</th></tr></thead>
      <tbody>${filasTrabajadores || '<tr><td colspan="6">Sin trabajadores</td></tr>'}</tbody>
    </table>
    <div class="firma">
      <div>Firma supervisor</div>
      <div>Firma V°B°</div>
    </div>
  </body></html>`;

  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;border:none;';
  document.body.appendChild(iframe);
  const doc = iframe.contentDocument || iframe.contentWindow?.document;
  if (!doc) { document.body.removeChild(iframe); return; }
  doc.open(); doc.write(html); doc.close();
  setTimeout(() => {
    iframe.contentWindow?.focus();
    iframe.contentWindow?.print();
    setTimeout(() => document.body.removeChild(iframe), 1000);
  }, 300);
}
